import { useState } from "react";
import { useNavigate } from "react-router";
import styled from "@emotion/styled";
import { api } from "@/modules/api";
import { fileModule } from "@/modules/file";
import { atlasRoutes } from "@/app/router/routes";
import { Button } from "@/components/button";

const CreatePageContainer = styled.div`
  padding: 20px;
  max-width: 800px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Label = styled.label`
  font-weight: 500;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
`;

const TextArea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  min-height: 100px;
`;

const FileInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  margin-top: 10px;
`;

const SuccessMessage = styled.div`
  color: #2e7d32;
  margin-top: 10px;
`;

const StepContainer = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
`;

const StepTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 15px;
`;

export const WebClipsCreatePage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step states
  const [capturedWebsiteId, setCapturedWebsiteId] = useState("");
  const [clippedTextContent, setClippedTextContent] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUploadedFileId, setImageUploadedFileId] = useState<string | null>(null);

  const handleUploadImage = async () => {
    if (!imageFile) {
      setError("Please select an image file");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { fileId } = await fileModule.uploadFile({ file: imageFile });
      setImageUploadedFileId(fileId);
      setIsLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!capturedWebsiteId) {
      setError("Please enter a captured website ID");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create the web clip
      const response = await api.post("/v2/web-clips", {
        body: {
          captured_website_id: capturedWebsiteId,
          clipped_text_content: clippedTextContent ?? undefined,
          clipped_image_uploaded_file_id: imageUploadedFileId ?? undefined,
          timestamp: new Date().toISOString(),
        },
      });

      if (response.error || !response.data) {
        throw new Error("Failed to create web clip");
      }

      // Navigate to the view page
      navigate(atlasRoutes.webClipsView({ params: { webClipId: response.data.web_clip_id } }).path);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setIsLoading(false);
    }
  };

  return (
    <CreatePageContainer>
      <PageTitle>Create Web Clip</PageTitle>

      <Form onSubmit={handleSubmit}>
        <StepContainer>
          <StepTitle>Step 1: Captured Website</StepTitle>
          <FormGroup>
            <Label htmlFor="capturedWebsiteId">Captured Website ID*</Label>
            <Input
              id="capturedWebsiteId"
              type="text"
              value={capturedWebsiteId}
              onChange={e => setCapturedWebsiteId(e.target.value)}
              placeholder="Enter UUID of captured website"
              required
            />
          </FormGroup>
        </StepContainer>

        <StepContainer>
          <StepTitle>Step 2: Clipped Content</StepTitle>
          <FormGroup>
            <Label htmlFor="textContent">Clipped Text Content</Label>
            <TextArea
              id="textContent"
              value={clippedTextContent}
              onChange={e => setClippedTextContent(e.target.value)}
              placeholder="Enter any text content from the web clip"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="imageFile">Clipped Image (Optional)</Label>
            <FileInput
              id="imageFile"
              type="file"
              accept="image/*"
              onChange={e => setImageFile(e.target.files?.[0] ?? null)}
            />
          </FormGroup>

          {imageFile && !imageUploadedFileId && (
            <Button type="button" onClick={handleUploadImage} disabled={isLoading}>
              {isLoading ? "Uploading Image..." : "Upload Image"}
            </Button>
          )}

          {imageUploadedFileId && (
            <SuccessMessage>
              Image uploaded successfully! File ID: {imageUploadedFileId}
            </SuccessMessage>
          )}
        </StepContainer>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <ButtonGroup>
          <Button type="submit" disabled={isLoading || !capturedWebsiteId}>
            Create Web Clip
          </Button>
          <Button type="button" onClick={() => navigate(atlasRoutes.webClips({}).path)}>
            Cancel
          </Button>
        </ButtonGroup>
      </Form>
    </CreatePageContainer>
  );
};
